import { useCallback, useEffect, useMemo } from "react";

import {
  currentLocaleAtom,
  DataViewKey,
  fetcherModeAtom,
  KpkMedia,
  selectedElements,
  SentryBreadcrumbCategory,
  useCurrentUser,
  useShare,
} from "@keepeek/commons";
import { Fields } from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";
import trim from "lodash/trim";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";

import { configSectionComponentDownloadManagerSelector } from "../../config/selectors";
import { downloadManagerFetcherSelector } from "../../fetcher/selectors/downloadManager";
import {
  askDownloadFormState,
  AskDownloadReasonUse,
  askDownloadStepState,
} from "../atoms/askDownloads";
import {
  askDownloadElementIdsSelector,
  askDownloadReasonSelector,
} from "../selectors/askDownloads";
import { createDownloadValidationRequest, isBlank, isEmailError } from "../utils";

type UseAskDownloads = {
  clickAskDownload: () => void;
  askDownloadStep: boolean;
  askDownloadElementIds: KpkMedia["id"][];
  setAskDownloadElementIds: (mediaIds: KpkMedia["id"][]) => void;
  backFromAskDownload: () => void;
  open: boolean;
  fields: Fields;
  isFieldsInError: () => boolean;
};

const useAskDownloads = (open: boolean, closeDownloadManager: () => void): UseAskDownloads => {
  const { isPublicVisitor } = useCurrentUser();
  const router = useRouter();
  const { isShare } = useShare(router.pathname, router.query.tokenId);
  const snackBar = useSnackbar();
  const { t } = useTranslation();
  const askDownloadStep = useRecoilValue(askDownloadStepState);
  const [formState, setFormState] = useRecoilState(askDownloadFormState);
  // TODO: update the selection from any source
  const setSetSelectedElements = useSetRecoilState(selectedElements(DataViewKey.SEARCH));
  const { enableAskDownload, makeRequiredCommentAskDownload, askDownloadResponsibleIds } =
    useRecoilValue(configSectionComponentDownloadManagerSelector) ?? {
      enableAskDownload: undefined,
      makeRequiredCommentAskDownload: undefined,
      askDownloadResponsibleIds: undefined,
    };
  const currentLocale = useRecoilValue(currentLocaleAtom);
  const useTypeAskDownloadConf = useRecoilValue(configSectionComponentDownloadManagerSelector);

  const filteredUseOptions = useMemo(() => {
    let temp = useTypeAskDownloadConf?.useTypeAskDownload
      ?.filter((u) => u.language === currentLocale)
      .map((u) => u.value || "");
    if (!temp || temp.length === 0) {
      temp = Object.keys(AskDownloadReasonUse).map((k) =>
        t(`download-manager.askDownload.use.${k}`),
      );
    }
    return temp;
  }, [currentLocale, t, useTypeAskDownloadConf?.useTypeAskDownload]);
  // set use value
  useEffect(() => {
    setFormState((old) => {
      let useValue = old.fields.use;
      if (
        (!useValue || !filteredUseOptions?.includes(useValue)) &&
        filteredUseOptions &&
        filteredUseOptions.length > 0
      ) {
        useValue = filteredUseOptions[0];
      }
      return {
        ...old,
        fields: {
          ...old.fields,
          use: useValue,
        },
      };
    });
  }, [filteredUseOptions, setFormState]);

  const [askDownloadElementIds, setAskDownloadElementIds] = useRecoilState(
    askDownloadElementIdsSelector,
  );
  const reason = useRecoilValue(askDownloadReasonSelector);
  const mode = useRecoilValue(fetcherModeAtom);
  const downloadManagerFetcherSelectorQuery = useRecoilValueLoadable(
    downloadManagerFetcherSelector(mode),
  );

  const resetFields = useCallback(() => {
    setFormState((old) => {
      return {
        fields: {
          use: old.fields.use,
          comment: "",
          email: "",
          firstName: "",
          lastName: "",
        },
        displayErrors: false,
      };
    });
  }, [setFormState]);

  useEffect(() => {
    // On close reset askDownload
    if (!open) {
      setAskDownloadElementIds([]);
      resetFields();
    }
  }, [open, resetFields, setAskDownloadElementIds]);

  const backFromAskDownload = () => {
    setAskDownloadElementIds([]);
  };

  const fields: Fields = useMemo(() => {
    return {
      use: {
        value: formState.fields.use,
        required: true,
        requiredError: formState.displayErrors && isBlank(formState.fields.use),
        options: filteredUseOptions,
        onChange: (value: string) => {
          setFormState((old) => {
            return {
              ...old,
              fields: {
                ...old.fields,
                use: value,
              },
            };
          });
        },
      },
      comment: {
        value: formState.fields.comment,
        required: makeRequiredCommentAskDownload || isPublicVisitor,
        requiredError:
          formState.displayErrors &&
          (makeRequiredCommentAskDownload || isPublicVisitor) &&
          isBlank(formState.fields.comment),
        onChange: (value: string) => {
          setFormState((old) => {
            return {
              ...old,
              fields: {
                ...old.fields,
                comment: value,
              },
            };
          });
        },
      },
      ...((isPublicVisitor || isShare) && {
        email: {
          value: formState.fields.email,
          required: true,
          emailFormatError: formState.displayErrors && isEmailError(formState.fields.email),
          requiredError: formState.displayErrors && isBlank(formState.fields.email),
          onChange: (value: string) => {
            setFormState((old) => {
              return {
                ...old,
                fields: {
                  ...old.fields,
                  email: value,
                },
              };
            });
          },
        },
        firstName: {
          value: formState.fields.firstName,
          required: true,
          requiredError: formState.displayErrors && isBlank(formState.fields.firstName),
          onChange: (value: string) => {
            setFormState((old) => {
              return {
                ...old,
                fields: {
                  ...old.fields,
                  firstName: value,
                },
              };
            });
          },
        },
        lastName: {
          value: formState.fields.lastName,
          required: true,
          requiredError: formState.displayErrors && isBlank(formState.fields.lastName),
          onChange: (value: string) => {
            setFormState((old) => {
              return {
                ...old,
                fields: {
                  ...old.fields,
                  lastName: value,
                },
              };
            });
          },
        },
      }),
    };
  }, [
    filteredUseOptions,
    formState.displayErrors,
    formState.fields.comment,
    formState.fields.email,
    formState.fields.firstName,
    formState.fields.lastName,
    formState.fields.use,
    isPublicVisitor,
    isShare,
    makeRequiredCommentAskDownload,
    setFormState,
  ]);

  const clickAskDownload = useCallback(() => {
    if (downloadManagerFetcherSelectorQuery.state === "hasValue") {
      const { postDownloadValidationRequests } = downloadManagerFetcherSelectorQuery.contents;
      // Be positive, close the modal
      closeDownloadManager();
      // clear data view selection
      setSetSelectedElements([]);
      // Clear fields state => keep use selection for UX
      resetFields();
      // Then launch download request
      createDownloadValidationRequest(
        postDownloadValidationRequests,
        askDownloadElementIds,
        trim(reason),
        trim(fields.email?.value),
        trim(fields.lastName?.value),
        trim(fields.firstName?.value),
        askDownloadResponsibleIds,
      );
      // Let the user know with notifications
      snackBar.enqueueSnackbar(t("download-manager.askDownload.created.label"), {
        variant: "info",
      });
      // Then clear askDownloadElementIds
      setAskDownloadElementIds([]);

      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.USER_INTERACTION,
        message: `clickAskDownload: ${askDownloadElementIds.join(",")}`,
      });
    }
  }, [
    askDownloadElementIds,
    askDownloadResponsibleIds,
    downloadManagerFetcherSelectorQuery.contents,
    downloadManagerFetcherSelectorQuery.state,
    fields.email?.value,
    fields.firstName?.value,
    fields.lastName?.value,
    reason,
    resetFields,
    setAskDownloadElementIds,
    closeDownloadManager,
    setSetSelectedElements,
    snackBar,
    t,
  ]);

  const isFieldsInError: () => boolean = useCallback(() => {
    if (!enableAskDownload || !askDownloadStep) {
      return false;
    }
    // update state to display error if there is any:
    setFormState((old) => {
      return {
        ...old,
        displayErrors: true,
      };
    });
    /**
     * we check the errors directly here because we cannot rely on the "requiredError" property available in the "Field" object because it will be updated only at the next render.
     */
    return (
      isBlank(fields.use?.value) ||
      ((makeRequiredCommentAskDownload || isPublicVisitor) && isBlank(fields.comment?.value)) ||
      (isPublicVisitor && (isBlank(fields.email?.value) || isEmailError(fields.email?.value))) ||
      (isPublicVisitor && isBlank(fields.firstName?.value)) ||
      (isPublicVisitor && isBlank(fields.lastName?.value))
    );
  }, [
    askDownloadStep,
    enableAskDownload,
    fields.comment?.value,
    fields.email?.value,
    fields.firstName?.value,
    fields.lastName?.value,
    fields.use?.value,
    isPublicVisitor,
    makeRequiredCommentAskDownload,
    setFormState,
  ]);

  return {
    clickAskDownload,
    askDownloadStep,
    askDownloadElementIds,
    setAskDownloadElementIds,
    backFromAskDownload,
    open,
    fields,
    isFieldsInError,
  };
};

export default useAskDownloads;
